import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/usePageSEO";
import { FOUNDER } from "@/data/founderEntity";
import {
  ArrowLeft, MapPin, Phone, Mail, Clock, Globe, ExternalLink
} from "lucide-react";

const MAPS_API_KEY = "AIzaSyD-IivpiQbJpu1M9OS2iVElhpD5fxYw_w8";
const FOLCROFT_LAT = 39.8876;
const FOLCROFT_LNG = -75.2838;

export const LocationPage = () => {
  usePageSEO(
    "Location & Contact | My Biz Consulting",
    "Visit My Biz Consulting at 304 Baltimore Avenue, Folcroft, PA 19032. Get directions, contact info, and business hours."
  );

  return (
    <div className="min-h-screen bg-white" data-testid="location-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" />
            <span className="text-sm text-neutral-400">Location & Contact</span>
          </Link>
          <Link to="/" className="text-neutral-300 hover:text-white text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 py-3">
        <ol className="flex items-center gap-2 text-xs text-neutral-500">
          <li><Link to="/" className="hover:text-neutral-900">Home</Link></li>
          <li>/</li>
          <li className="text-neutral-900 font-medium" aria-current="page">Location & Contact</li>
        </ol>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Location & Contact</h1>
        <p className="text-neutral-600 mb-8">Visit us, get directions, or reach out to start a conversation about your business goals.</p>

        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          {/* Contact Info */}
          <div className="space-y-4">
            <Card className="border-neutral-200">
              <CardContent className="p-5 space-y-4">
                <h2 className="font-bold text-neutral-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#2BC40E]" /> Office Address
                </h2>
                <div className="text-sm text-neutral-700 leading-relaxed">
                  <p className="font-semibold">My Biz Consulting LLC</p>
                  <p>304 Baltimore Avenue</p>
                  <p>Folcroft, PA 19032</p>
                </div>
                <a href="https://maps.google.com/?q=304+Baltimore+Avenue+Folcroft+PA+19032" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[#2BC40E] hover:underline">
                  Get Directions <ExternalLink className="w-3 h-3" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-neutral-200">
              <CardContent className="p-5 space-y-3">
                <h2 className="font-bold text-neutral-900 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#2BC40E]" /> Contact
                </h2>
                <a href="tel:215-443-1076" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#2BC40E]">
                  <Phone className="w-4 h-4" /> 215-443-1076
                </a>
                <a href="mailto:Jamilah@mybizconsulting.net" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#2BC40E]">
                  <Mail className="w-4 h-4" /> Jamilah@mybizconsulting.net
                </a>
                <a href="https://www.mybizconsulting.net" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-[#2BC40E]">
                  <Globe className="w-4 h-4" /> www.mybizconsulting.net
                </a>
              </CardContent>
            </Card>

            <Card className="border-neutral-200">
              <CardContent className="p-5 space-y-3">
                <h2 className="font-bold text-neutral-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#2BC40E]" /> Business Hours
                </h2>
                <div className="text-sm text-neutral-700 space-y-1">
                  <div className="flex justify-between"><span>Monday - Friday</span><span className="font-medium">9:00 AM - 6:00 PM EST</span></div>
                  <div className="flex justify-between"><span>Saturday</span><span className="text-neutral-500">By Appointment</span></div>
                  <div className="flex justify-between"><span>Sunday</span><span className="text-neutral-500">Closed</span></div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-2">
              <Link to="/clarity-assessment">
                <Button className="w-full bg-[#333333] hover:bg-[#444444] text-white">Start Free Assessment</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="w-full border-neutral-300">View Pricing</Button>
              </Link>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: "600px" }}>
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=304+Baltimore+Avenue,Folcroft,PA+19032&zoom=15`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="My Biz Consulting Location - 304 Baltimore Avenue, Folcroft, PA 19032"
              />
            </div>
            <p className="text-xs text-neutral-500 text-center mt-2">
              304 Baltimore Avenue, Folcroft, PA 19032 — Philadelphia Metro Area
            </p>
          </div>
        </div>

        {/* Virtual Services Note */}
        <Card className="border-[#39FF14]/20 bg-gradient-to-r from-[#39FF14]/5 to-emerald-50">
          <CardContent className="p-6 text-center">
            <h2 className="font-bold text-neutral-900 mb-2">We Serve Clients Nationwide</h2>
            <p className="text-sm text-neutral-600 max-w-xl mx-auto">
              While our office is in Folcroft, PA, we work with entrepreneurs, entertainers, and influencers
              across the United States through virtual consulting sessions, AI-powered tools, and our online Business Solutions Center.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LocationPage;
